/*
* This HOC is used to call an API action and abort it if needed (on unLoad of the component).
* A single AbortController is created for each component and for every api it passes the controller's signal to the action.
* Should not use this for every API call, only for those that can be aborted without side effects.
* Ex: if the API call is used to fetch data (especially heavy queries), it is a good candidate to be aborted.  If the API call is used to
* update data, it is not a good candidate to be aborted.
*/

import React, { useRef, useEffect } from 'react';

export const withAbortableApi = WrappedComponent => {

  const Wrapper = (props) => {

    const controllerRef = useRef<AbortController>(new AbortController());

    useEffect(() => {
      // This function runs after the component renders (like componentDidMount)

      // This is the cleanup function (like componentWillUnmount)
      return () => {
        controllerRef.current.abort();
      };
    }, []);

    const callAbortableApi = async (callback) => {
      return await callback(controllerRef.current.signal);
    };

    return (
      <WrappedComponent callAbortableApi={callAbortableApi} {...props} />
    );
  }

  Wrapper.displayName = 'AbortableApiWrapper';
  return Wrapper;
}

/*
  Usage:

  Class based component:
  
  1) In the component where you want to make an abortable API call, import the HOC:
  import { withAbortableApi } from '@Common/AbortableApi';

  2) Wrap your component with it (note this example also uses react-router and redux):
  export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withAbortableApi(Projects)));

  3) In the actions file, create a new or add to an existing action a signal parameter:
  searchResponsibleUsers: (locationKeys, searchTerm, signal) =>
    (dispatch) => {
        return dispatch({
            [CALL_API]: {
                endpoint: 'location-task/search-responsible-users',
                method: 'POST',
                body: { locationKeys: locationKeys, searchTerm: searchTerm },
                types: [
                    types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_REQUEST,
                    types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_SUCCESS,
                    types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_FAILURE
                ],
                signal,
            }
        });
    },

  4) In the component, use the callAbortableApi function to make the API call.  Note that the callAbortableApi function is passed as a prop to the component:
const response = await this.props.callAbortableApi((signal) => this.props.companyDashboardActions.checkSettingsAvailabilityV2(settings, signal));

*/
export function useSafeAsyncEffect(effect: (signal: AbortSignal) => void | Promise<void>, deps: any[] = []) {
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortControllerRef.current = new AbortController();

    const runEffect = async () => {
      try {
        await effect(abortControllerRef.current!.signal);
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.debug('Request aborted');
        } else {
          console.error('useSafeAsyncEffect error:', error);
        }
      }
    };

    runEffect();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, deps);
}
